import { QRCodeSVG } from "qrcode.react";
import { LOGO_URL } from "@/lib/brand";
import { AEP_INTRO, buildConclusao } from "@/lib/aepContent";
import RiskMatrix from "@/components/RiskMatrix";
import RiskMatrixFMEA from "@/components/RiskMatrixFMEA";
import { SECTIONS, RiskTag, SolluteLeaf } from "./shared";

/** Public URL the QR code on the AEP points to for third-party verification. */
function validateUrl(aid) {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/validar/${aid}`;
}

/* ---------------- Capa ---------------- */
export function AEPCover({ a, company, date, hash }) {
  return (
    <section className="aep-cover page-break -mx-10 -my-8 mb-12 print:m-0" style={{ minHeight: "calc(100vh - 100px)" }}>
      {/* Layered background */}
      <div className="aep-cover-bg"></div>
      <div className="aep-cover-grid"></div>
      <div className="aep-cover-leaf"></div>

      {/* Decorative SVG leaves scattered as artistic field */}
      <SolluteLeaf className="aep-cover-leaf-svg leaf-1" color="#8BC53F" opacity={0.10} rotate={-22} />
      <SolluteLeaf className="aep-cover-leaf-svg leaf-2" color="#A8E063" opacity={0.07} rotate={18} />
      <SolluteLeaf className="aep-cover-leaf-svg leaf-3" color="#8BC53F" opacity={0.08} rotate={-45} />
      <SolluteLeaf className="aep-cover-leaf-svg leaf-4" color="#FFFFFF" opacity={0.05} rotate={62} />

      {/* Vertical brand rule with marque tag */}
      <div className="aep-cover-marque">
        <div className="aep-cover-marque-line"></div>
        <span>SOLLUTE · OCUPACIONAL</span>
        <div className="aep-cover-marque-line"></div>
      </div>

      <div className="aep-cover-content px-12 py-16 lg:px-20 lg:py-24 text-white">

        {/* Top row: client logo (if any) or filing tag — NEVER duplicates Sollute */}
        <div className="flex items-start justify-between mb-12 gap-6">
          {company.logo_base64 ? (
            <div className="bg-white/95 p-3 shadow-2xl" data-testid="aep-cover-client-logo">
              <img src={company.logo_base64}
                   alt={company.razao_social || "Empresa avaliada"}
                   className="h-14 w-auto object-contain max-w-[180px]" />
            </div>
          ) : (
            <div className="text-[10px] tracking-[0.4em] text-emerald-200/70 uppercase font-bold pt-2">
              Documento controlado · uso interno autorizado
            </div>
          )}
          <div className="text-right">
            <div className="text-[10px] tracking-[0.4em] text-emerald-200 uppercase font-bold">
              Documento técnico consolidado
            </div>
            <div className="text-[10px] tracking-[0.3em] text-emerald-300/70 uppercase mt-1">
              NR-01 · Riscos Psicossociais
            </div>
          </div>
        </div>

        {/* Hero composition: title left, big artistic Sollute logo right (single Sollute mark) */}
        <div className="grid grid-cols-12 gap-6 items-center mb-12">
          <div className="col-span-7 relative">
            <h1 className="font-display text-6xl lg:text-8xl font-extrabold tracking-tight leading-[0.95]">
              AEP
            </h1>
            <div className="aep-cover-rule"></div>
            <div className="text-[26px] lg:text-[34px] font-display font-extrabold tracking-tight leading-tight mt-3">
              <div className="text-emerald-200/90">Análise</div>
              <div className="text-emerald-200/90">Ergonômica</div>
              <div className="text-[#8BC53F]">Preliminar</div>
            </div>
          </div>

          <div className="col-span-5 flex justify-center relative" data-testid="aep-cover-sollute-mark">
            <div className="aep-cover-logo-halo"></div>
            <div className="aep-cover-logo-card">
              <img src={LOGO_URL} alt="Sollute Ocupacional"
                   className="aep-cover-logo-img" />
            </div>
          </div>
        </div>

        <p className="text-emerald-50/90 text-base lg:text-lg max-w-2xl leading-relaxed mb-12">
          Avaliação consolidada dos fatores de riscos psicossociais
          no contexto do gerenciamento de riscos ocupacionais e
          organização do trabalho.
        </p>

        {/* Data grid */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-6 max-w-3xl border-t border-emerald-300/30 pt-8">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80 mb-1">Empresa avaliada</div>
            <div className="text-xl font-bold">{company.razao_social || "—"}</div>
            {company.nome_fantasia && <div className="text-sm text-emerald-100/80 mt-1">{company.nome_fantasia}</div>}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80 mb-1">CNPJ</div>
            <div className="font-mono text-base">{company.cnpj || "—"}</div>
            {company.cnae && <div className="text-xs text-emerald-100/70 mt-1">CNAE {company.cnae}{company.grau_risco ? ` · Grau ${company.grau_risco}` : ""}</div>}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80 mb-1">Endereço</div>
            <div className="text-sm leading-relaxed">
              {company.endereco ? (
                <>
                  {company.endereco}{company.numero ? `, ${company.numero}` : ""}{company.complemento ? ` — ${company.complemento}` : ""}<br/>
                  {[company.bairro, company.cidade && company.uf ? `${company.cidade}/${company.uf}` : company.cidade || company.uf].filter(Boolean).join(" · ")}
                  {company.cep && <span className="font-mono"> · CEP {company.cep}</span>}
                </>
              ) : (company.unidade || "—")}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80 mb-1">Responsável técnico</div>
            <div className="text-base">{a.responsavel || company.responsavel_tecnico || "—"}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80 mb-1">Data de emissão</div>
            <div className="text-base font-mono">{date}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300/80 mb-1">Hash de integridade</div>
            <div className="text-base font-mono">{hash}</div>
          </div>
        </div>

        {/* QR for public verification — printed bright on a white tile so it
            scans reliably even when the page is dark. */}
        <div className="mt-10 flex items-center gap-4" data-testid="aep-cover-qr">
          <div className="bg-white p-2 shrink-0">
            <QRCodeSVG value={validateUrl(a.id)} size={88} level="M" includeMargin={false} />
          </div>
          <div className="text-[11px] tracking-[0.15em] uppercase text-emerald-200/80 leading-relaxed">
            Verifique a autenticidade<br/>
            <span className="text-white font-bold normal-case tracking-normal text-sm">
              {validateUrl(a.id).replace(/^https?:\/\//, "")}
            </span>
          </div>
        </div>

        <div className="absolute bottom-8 left-12 lg:left-20 right-12 lg:right-20 flex justify-between items-end text-[10px] uppercase tracking-[0.3em] text-emerald-300/60">
          <span>NR-01 · NR-17 · LGPD</span>
          <span>sollute · ocupacional</span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Sumário ---------------- */
export function AEPSummary({ included }) {
  const items = SECTIONS.filter((s) => included[s.k] && s.k !== "capa" && s.k !== "sumario");
  return (
    <section className="page-break mb-12 avoid-break">
      <div className="overline mb-2 text-sollute-green">Página 2</div>
      <h2 className="font-display text-3xl font-extrabold tracking-tight mb-6 text-slate-900">Sumário do documento</h2>
      <ol className="space-y-2 text-sm">
        {items.map((s, i) => (
          <li key={s.k} className="flex justify-between items-baseline border-b border-dotted border-slate-300 py-1.5">
            <span className="flex-1">
              <span className="font-mono text-slate-400 mr-3 inline-block w-6 text-right">{String(i + 1).padStart(2, "0")}</span>
              <a href={`#sec-${s.k}`} className="hover:text-sollute-green">{s.label}</a>
            </span>
            <span className="font-mono text-slate-400 text-xs ml-3">
              <span className="print:hidden">§{String(i + 1).padStart(2, "0")}</span>
              <a href={`#sec-${s.k}`} className="toc-num hidden print:inline"></a>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-6 text-xs text-slate-500 italic">
        Documento diagramado para impressão em A4, com layout executivo, estrutura compatível com análise documental por gestores, clientes e auditores do MTE.
      </p>
    </section>
  );
}

/* ---------------- Dashboard ---------------- */
export function AEPDashboard({ data, score100, criticalCount }) {
  const kpis = [
    ["Avaliações", data.response_count],
    ["Grupos", 1],
    ["Risco médio", `${score100}%`],
    ["Críticas", criticalCount],
    ["Classe geral", data.overall.label],
  ];
  return (
    <section id="sec-dashboard" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6 text-slate-900">Dashboard executivo — visão consolidada</h2>
      <div className="grid grid-cols-5 gap-3 mb-6">
        {kpis.map(([l, v]) => (
          <div key={l} className="border border-slate-300 p-3 text-center">
            <div className="kpi-number text-2xl text-emerald-900">{v || "—"}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{l}</div>
          </div>
        ))}
      </div>
      <div className="overline mb-3">Mapa executivo das dimensões</div>
      <div className="grid grid-cols-3 gap-2">
        {data.dimensions.map((d) => (
          <div key={d.dimensao} className={`${d.responses ? `risk-${d.level}` : "risk-0"} border p-3`}>
            <div className="text-[10px] uppercase tracking-widest font-bold">{d.label}</div>
            <div className="font-display text-xl font-extrabold mt-1">{Math.round((d.score / 5) * 100)}%</div>
            <div className="text-[10px] mt-1">{d.dimensao}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Controle documental ---------------- */
export function AEPControleDoc({ a, company, date, hash }) {
  const rows = [
    ["Código do documento", hash],
    ["Versão / Revisão", "Rev. 00 – Emissão inicial"],
    ["Data de emissão", date],
    ["Próxima revisão recomendada", "12 meses após emissão"],
    ["Elaborado por", "Sollute Ocupacional"],
    ["Responsável técnico", a.responsavel || company.responsavel_tecnico || "—"],
    ["Hash de integridade", hash],
  ];
  return (
    <section id="sec-controle_doc" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Controle documental e rastreabilidade</h2>
      <table className="w-full text-sm border border-slate-200">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-slate-100">
              <td className="py-2 px-3 font-semibold text-slate-700 bg-slate-50 w-72">{k}</td>
              <td className="py-2 px-3 font-mono text-slate-800">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* ---------------- Resumo executivo ---------------- */
function priorityLabel(level) {
  if (level >= 4) return "Imediata";
  if (level >= 3) return "Curto prazo";
  return "Rotina";
}

export function AEPResumoExec({ data, score100, criticalCount, focoDim }) {
  const priorLabel = priorityLabel(data.overall.level);
  return (
    <section id="sec-resumo" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6 text-slate-900">Resumo executivo para diretoria</h2>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-slate-300 p-4 text-center">
          <div className="kpi-number text-4xl text-emerald-900">{score100}%</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">Risco médio consolidado</div>
        </div>
        <div className="border border-slate-300 p-4 text-center">
          <div className="font-display text-2xl font-extrabold mt-2">{data.overall.label}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">Classe predominante</div>
        </div>
        <div className="border border-slate-300 p-4 text-center">
          <div className="font-display text-2xl font-extrabold mt-2">{priorLabel}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">Prioridade de gestão</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6 text-sm">
        <div>
          <div className="overline mb-2">Conclusão executiva automática</div>
          <p className="text-slate-700 leading-relaxed">
            {buildConclusao(data.overall.label, data.overall_score, criticalCount, focoDim)}
          </p>
        </div>
        <div>
          <div className="overline mb-2">Decisões esperadas da direção</div>
          <ul className="text-slate-700 space-y-1 list-disc pl-5">
            <li>Validar responsáveis e prazos do plano de ação.</li>
            <li>Garantir recursos para medidas organizacionais prioritárias.</li>
            <li>Acompanhar indicadores de jornada, absenteísmo, conflitos e afastamentos.</li>
            <li>Registrar evidências de implantação e reavaliar periodicamente.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Prioridades ---------------- */
export function AEPPrioridades({ ranked }) {
  const top5 = ranked.slice(0, 5);
  return (
    <section id="sec-prioridades" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">5 prioridades recomendadas</h2>
      <div className="space-y-3">
        {top5.map((d, i) => (
          <div key={d.dimensao} className="flex items-start gap-3 border border-slate-200 p-3">
            <span className="font-display text-2xl font-extrabold text-emerald-900 w-8">{i + 1}.</span>
            <div className="flex-1">
              <div className="font-bold text-slate-900">{d.dimensao} ({Math.round((d.score / 5) * 100)}%)</div>
              <div className="text-xs text-slate-600 mt-1">Revisar políticas, processos e práticas de gestão relacionadas a esta dimensão. Detalhar plano de ação específico com responsáveis, prazos e evidências.</div>
            </div>
            <RiskTag level={d.level} label={d.label} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Rastreabilidade ---------------- */
export function AEPRastreabilidade({ ranked, cnpj }) {
  const [overrides, setOverrides] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const cnpjDigits = useMemo(() => (cnpj || "").replace(/\D/g, ""), [cnpj]);
  const canEdit = !!cnpjDigits;

  const slugify = useCallback((s) => {
    return (s || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "")
      .slice(0, 120);
  }, []);

  useEffect(() => {
    if (!canEdit) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/cnpj/${cnpjDigits}/aep-rastreabilidade`);
        if (alive) setOverrides(res.data || {});
      } catch (_) { /* silencioso */ }
    })();
    return () => { alive = false; };
  }, [cnpjDigits, canEdit]);

  const valFor = (dim, field, fallback) => {
    const o = overrides[slugify(dim)];
    if (o && typeof o[field] === "string" && o[field].trim()) return o[field];
    return fallback;
  };

  const save = async (dim, patch) => {
    if (!canEdit) return;
    const slug = slugify(dim);
    setSavingKey(`${slug}:${Object.keys(patch).join(",")}`);
    try {
      const res = await api.put(`/cnpj/${cnpjDigits}/aep-rastreabilidade/${encodeURIComponent(dim)}`, patch);
      setOverrides((s) => ({ ...s, [slug]: res.data }));
    } catch (e) {
      console.warn("rastreabilidade save failed", e);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section id="sec-rastreabilidade" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-2 text-slate-900">Quadro de rastreabilidade técnica</h2>
      <p className="text-xs text-slate-600 mb-3">
        Ligação objetiva entre fator de risco, evidência utilizada, classificação, medida preventiva, responsável e prazo.
        {canEdit && <span className="text-emerald-700 no-print"> · Clique em <b>Responsável</b> ou <b>Prazo</b> para editar — salva automaticamente.</span>}
      </p>
      <table className="w-full text-xs border border-slate-200">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-300">
            <th className="text-left p-2 font-bold uppercase">Fator / Dimensão</th>
            <th className="text-left font-bold uppercase">Evidência</th>
            <th className="text-center font-bold uppercase">Class.</th>
            <th className="text-left font-bold uppercase">Medida de controle</th>
            <th className="text-left font-bold uppercase">Responsável</th>
            <th className="text-left font-bold uppercase">Prazo</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((d) => {
            const slug = slugify(d.dimensao);
            const respValue = valFor(d.dimensao, "responsavel", "Direção / RH / SESMT");
            const prazoValue = valFor(d.dimensao, "prazo", "12 meses");
            return (
              <tr key={d.dimensao} className="border-b border-slate-100 align-top">
                <td className="py-2 px-2 font-semibold">{d.dimensao}</td>
                <td className="py-2 px-2 text-slate-600">Consolidado {Math.round((d.score / 5) * 100)}% · {d.responses} resp.</td>
                <td className="py-2 px-2 text-center"><RiskTag level={d.level} label={d.label} /></td>
                <td className="py-2 px-2 text-slate-700">Revisar políticas, processos e práticas de gestão da dimensão. Implantar plano de ação cronológico.</td>
                <td className="py-2 px-2">
                  {canEdit ? (
                    <EditableCell
                      value={respValue}
                      placeholder="Direção / RH / SESMT"
                      isSaving={savingKey === `${slug}:responsavel`}
                      onSave={(v) => save(d.dimensao, { responsavel: v })}
                      testId={`rastr-resp-${slug}`}
                    />
                  ) : respValue}
                </td>
                <td className="py-2 px-2 font-mono">
                  {canEdit ? (
                    <EditableCell
                      value={prazoValue}
                      placeholder="12 meses"
                      isSaving={savingKey === `${slug}:prazo`}
                      onSave={(v) => save(d.dimensao, { prazo: v })}
                      testId={`rastr-prazo-${slug}`}
                    />
                  ) : prazoValue}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function EditableCell({ value, placeholder, isSaving, onSave, testId }) {
  const [draft, setDraft] = useState(value || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  const commit = () => {
    setEditing(false);
    const v = (draft || "").trim();
    if (v !== (value || "").trim()) onSave(v);
  };
  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
          if (e.key === "Escape") { setDraft(value || ""); setEditing(false); }
        }}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className="w-full bg-white border border-emerald-400 rounded px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-emerald-400"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={testId}
      className="w-full text-left no-print:hover:bg-emerald-50 px-1 py-0.5 rounded text-slate-700 hover:bg-emerald-50 transition print:hover:bg-transparent"
      title="Clique para editar"
    >
      {value || <span className="text-slate-400 italic">{placeholder}</span>}
      {isSaving && <span className="ml-2 text-[10px] text-emerald-600 no-print">salvando…</span>}
    </button>
  );
}

/* ---------------- Heatmap ---------------- */
export function AEPHeatmap({ ranked, critByDim, maxResp }) {
  return (
    <section id="sec-heatmap" className="mb-12 avoid-break">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-2 text-slate-900">Mapa de calor por dimensão</h2>
      <p className="text-xs text-slate-600 mb-4">
        Distribuição de severidade com indicador de score consolidado e contagem absoluta de respostas críticas (≥4) por dimensão psicossocial.
      </p>
      <div className="space-y-3">
        <div className="grid grid-cols-12 gap-3 items-center text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2">
          <div className="col-span-3">Dimensão</div>
          <div className="col-span-6">Severidade média (verde-claro = cobertura)</div>
          <div className="col-span-1 text-center">Classe</div>
          <div className="col-span-1 text-center">Score</div>
          <div className="col-span-1 text-center">Críticas</div>
        </div>
        {ranked.map((d) => {
          const pct = Math.round((d.score / 5) * 100);
          const respPct = (d.responses / maxResp) * 100;
          const crit = critByDim[d.dimensao] || 0;
          return (
            <div key={d.dimensao} className="grid grid-cols-12 gap-3 items-center text-xs">
              <div className="col-span-3 font-semibold text-slate-800 leading-tight">{d.dimensao}</div>
              <div className="col-span-6">
                <div className="h-7 bg-slate-100 border border-slate-200 relative overflow-hidden">
                  <div className={`h-full risk-${d.level} flex items-center justify-end pr-2`}
                       style={{ width: `${pct}%` }}>
                    <span className="text-[10px] font-bold uppercase tracking-wider">{pct}%</span>
                  </div>
                </div>
                <div className="h-1 bg-slate-100 mt-1">
                  <div className="h-full bg-sollute-leaf" style={{ width: `${respPct}%` }}></div>
                </div>
              </div>
              <div className="col-span-1 text-center">
                <span className={`risk-${d.level} border px-2 py-0.5 font-bold uppercase tracking-wider text-[9px]`}>{d.label}</span>
              </div>
              <div className="col-span-1 text-center font-mono font-bold text-slate-800">{d.score}</div>
              <div className="col-span-1 text-center">
                {crit > 0 ? (
                  <span className="bg-red-100 text-red-800 border border-red-300 px-2 py-0.5 font-bold text-[10px]">
                    <i className="fa-solid fa-triangle-exclamation mr-1"></i>{crit}
                  </span>
                ) : (
                  <span className="text-slate-400 text-[10px]">—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Timeline ---------------- */
export function AEPTimeline({ timelineBuckets }) {
  return (
    <section id="sec-timeline" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Timeline do plano de ação</h2>
      <div className="grid grid-cols-4 gap-3">
        {["30 dias", "60 dias", "90 dias", "180 dias"].map((p) => {
          const cnt = timelineBuckets[p] || 0;
          return (
            <div key={p} className="border border-slate-300 p-3">
              <div className="overline text-emerald-900">{p}</div>
              <div className="font-display text-2xl font-extrabold mt-1">{cnt}</div>
              <div className="text-xs text-slate-600 mt-1">{cnt === 0 ? "Monitoramento" : "ação(ões) prevista(s)"}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Checklist ---------------- */
export function AEPChecklist({ hasActions }) {
  const items = [
    "Identificação da empresa, CNPJ, unidade e responsável técnico preenchida.",
    "Base individual importada e consolidada.",
    "Plano de ação cronológico disponível.",
    "Classificação permite monitoramento conforme cronograma.",
    "Introdução e metodologia AEP com referências à NR-01 e NR-17.",
    "Dimensões psicossociais reconhecidas e classificadas.",
    hasActions ? "Plano de ação documentado e atribuído." : "Plano de ação pendente de criação.",
    "Assinaturas, hash e validação documental disponíveis.",
  ];
  return (
    <section id="sec-checklist" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Checklist final de auditoria MTE</h2>
      <div className="grid grid-cols-2 gap-2 text-sm">
        {items.map((item) => (
          <div key={item} className="flex items-start gap-2">
            <i className="fa-solid fa-check text-emerald-700 mt-0.5"></i>
            <span className="text-slate-700">{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- LGPD ---------------- */
export function AEPLgpd() {
  return (
    <section id="sec-lgpd" className="mb-12 page-break">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Termo de confidencialidade, LGPD e limites de uso</h2>
      <p className="text-slate-700 leading-relaxed whitespace-pre-line">{AEP_INTRO.termo_lgpd}</p>
    </section>
  );
}

/* ---------------- Introdução técnica ---------------- */
const INTRO_BLOCKS_1 = [
  ["1. Introdução", "introducao"],
  ["2. Objetivo", "objetivo"],
  ["3. Metodologia utilizada", "metodologia"],
];
const INTRO_BLOCKS_2 = [
  ["4. Avaliação dos fatores de riscos psicossociais", "avaliacao"],
  ["5. Categorização do fator de risco", "categorizacao"],
  ["6. Classificação da probabilidade", "probabilidade"],
  ["7. Classificação da gravidade", "gravidade"],
  ["8. Classificação dos perigos/fatores de riscos", "classificacao"],
  ["9. Controle dos riscos", "controle"],
  ["10. Implantação das medidas de prevenção", "implantacao"],
];

export function AEPIntro() {
  return (
    <section id="sec-intro" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6 text-slate-900">Introdução técnica em formato de AEP</h2>
      {INTRO_BLOCKS_1.map(([t, key]) => (
        <div key={t} className="mb-4">
          <h3 className="font-bold text-slate-900 mt-3">{t}</h3>
          <p className="text-slate-700 leading-relaxed whitespace-pre-line">{AEP_INTRO[key]}</p>
        </div>
      ))}
      <ul className="list-disc pl-6 text-slate-700 leading-relaxed">
        {AEP_INTRO.metodologia_passos.map((p) => <li key={p}>{p}</li>)}
      </ul>
      {INTRO_BLOCKS_2.map(([t, key]) => (
        <div key={t} className="mb-4">
          <h3 className="font-bold text-slate-900 mt-3">{t}</h3>
          <p className="text-slate-700 leading-relaxed">{AEP_INTRO[key]}</p>
        </div>
      ))}
      <h3 className="font-bold text-slate-900 mt-3">11. Medidas preventivas — ordem coletiva/organizacional</h3>
      <ul className="list-disc pl-6 text-slate-700">{AEP_INTRO.coletivas.map((p) => <li key={p}>{p}</li>)}</ul>
      <h3 className="font-bold text-slate-900 mt-3">12. Medidas preventivas — ordem administrativa</h3>
      <ul className="list-disc pl-6 text-slate-700">{AEP_INTRO.administrativas.map((p) => <li key={p}>{p}</li>)}</ul>
      <h3 className="font-bold text-slate-900 mt-3">13. Medidas preventivas — ordem individual</h3>
      <p className="text-slate-700 leading-relaxed">{AEP_INTRO.individuais}</p>
      <h3 className="font-bold text-slate-900 mt-3">14. Plano de ação cronológico</h3>
      <p className="text-slate-700 leading-relaxed">{AEP_INTRO.plano}</p>
      <h3 className="font-bold text-slate-900 mt-3">15. Anexos</h3>
      <p className="text-slate-700 leading-relaxed">{AEP_INTRO.anexos}</p>
      <h3 className="font-bold text-slate-900 mt-3">16. Referências bibliográficas e normativas</h3>
      <ul className="list-disc pl-6 text-slate-700">{AEP_INTRO.referencias.map((p) => <li key={p}>{p}</li>)}</ul>
    </section>
  );
}

/* ---------------- Base importada ---------------- */
export function AEPBase({ responses, totalQuestions }) {
  // Fallback quando o backend não informa question_count por resposta (ex.:
  // AEPReport per-assessment). No consolidado por CNPJ cada respondente traz
  // o próprio `question_count` — usamos ele para exibir "22/22" ao invés de
  // "22/54" (soma consolidada de instrumentos diferentes).
  const fallback = totalQuestions || 54;
  // Detecta se há múltiplos instrumentos misturados para adicionar a coluna
  // "Instrumento" (útil quando o CNPJ combinou Teste Rápido + NR-01 Sollute).
  const uniqueTests = new Set(responses.map((r) => r.test_name).filter(Boolean));
  const showInstrumentCol = uniqueTests.size > 1;
  return (
    <section id="sec-base" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Base importada e revisão dos dados</h2>
      <table className="w-full text-xs border border-slate-200">
        <thead className="bg-slate-50 border-b border-slate-300">
          <tr>
            <th className="text-left p-2 font-bold uppercase">#</th>
            <th className="text-left font-bold uppercase">Respondente</th>
            <th className="text-left font-bold uppercase">Matrícula</th>
            {showInstrumentCol && <th className="text-left font-bold uppercase">Instrumento</th>}
            <th className="text-center font-bold uppercase">Submetido em</th>
            <th className="text-center font-bold uppercase">Respostas</th>
          </tr>
        </thead>
        <tbody>
          {responses.map((r, i) => {
            const total = r.question_count || fallback;
            return (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="p-2 font-mono">{i + 1}</td>
                <td className="p-2">{r.employee_name || <span className="italic text-slate-400">anônimo</span>}</td>
                <td className="p-2 font-mono text-xs">{r.employee_matricula || "—"}</td>
                {showInstrumentCol && <td className="p-2 text-xs">{r.test_name || "—"}</td>}
                <td className="p-2 text-center font-mono text-xs">{new Date(r.submitted_at).toLocaleString("pt-BR")}</td>
                <td className="p-2 text-center font-mono">{r.answers.length}/{total}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/* ---------------- Consolidação ---------------- */
export function AEPConsolidacao({ data }) {
  return (
    <section id="sec-consolidacao" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Consolidação por dimensão psicossocial</h2>
      <table className="w-full text-sm border border-slate-200">
        <thead className="bg-slate-50 border-b border-slate-300">
          <tr>
            <th className="text-left p-2 font-bold uppercase">Dimensão</th>
            <th className="text-center font-bold uppercase">Avaliações</th>
            <th className="text-center font-bold uppercase">Percentual médio</th>
            <th className="text-center font-bold uppercase">Classificação</th>
          </tr>
        </thead>
        <tbody>
          {data.dimensions.map((d) => (
            <tr key={d.dimensao} className="border-b border-slate-100">
              <td className="p-2 font-semibold">{d.dimensao}</td>
              <td className="text-center font-mono">{d.responses}</td>
              <td className="text-center font-mono font-bold">{Math.round((d.score / 5) * 100)}%</td>
              <td className="text-center"><RiskTag level={d.responses ? d.level : 0} label={d.label} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/* ---------------- Matriz de risco ---------------- */
export function AEPMatriz({ data, criticalCount, matrixType = "pxs", expectedWorkers = 0 }) {
  const isFmea = matrixType === "fmea";
  return (
    <section id="sec-matriz" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-2 text-slate-900">
        {isFmea
          ? "Matriz FMEA consolidada (S × O × D)"
          : "Matriz de risco AIHA consolidada"}
      </h2>
      <p className="text-xs text-slate-600 mb-4">
        {isFmea
          ? "Metodologia FMEA — Failure Mode and Effects Analysis (NBR/IEC 60812). Severidade (S, 1-10), Ocorrência (O, 1-10) e Detecção (D, 1-10) compõem o Risk Priority Number (RPN = S × O × D, faixa 1-1000). Classificação: ≤50 Baixo · ≤125 Moderado · ≤250 Alto · >250 Crítico. Ajuste técnico deve ser realizado pelo responsável conforme evidências de campo."
          : "Probabilidade (P1-P4) × Severidade (S1-S4). Probabilidade estimada pelo percentual médio de risco consolidado e recorrência das respostas críticas; severidade pela natureza do fator psicossocial agravada por percentuais elevados. Ajuste técnico deve ser realizado pelo responsável conforme evidências de campo."}
      </p>
      {isFmea ? (
        <RiskMatrixFMEA
          overallScore={data.overall_score}
          dimensions={data.dimensions}
          criticalCount={criticalCount}
          totalResponses={data.response_count}
          expectedWorkers={expectedWorkers}
          initialOverrides={data.assessment?.fmea_overrides || {}}
          editable={false}
          showLegend={true}
          strict={Boolean(data.assessment?.aiha_strict)}
        />
      ) : (
        <RiskMatrix
          overallScore={data.overall_score}
          dimensions={data.dimensions}
          criticalCount={criticalCount}
          totalResponses={data.response_count}
          showLegend={true}
          strict={Boolean(data.assessment?.aiha_strict)}
        />
      )}
    </section>
  );
}

/* ---------------- Plano de ação ---------------- */
export function AEPPlano({ actions, cnpj, overrides, onOverrideChange, onActionsChanged }) {
  // Mirror actions locally so edits/deletes reflect instantly even when the
  // parent doesn't pass an onActionsChanged callback (per-assessment AEP).
  const [localActions, setLocalActions] = useState(actions);
  useEffect(() => { setLocalActions(actions); }, [actions]);

  const [editing, setEditing] = useState(null); // action id being edited
  const [draft, setDraft] = useState(null);     // working copy
  const [saving, setSaving] = useState(false);

  const startEdit = (act) => {
    setEditing(act.id);
    setDraft({
      assessment_id: act.assessment_id,
      dimensao: act.dimensao || "",
      acao: act.acao || "",
      prioridade: act.prioridade || "Média",
      responsavel: act.responsavel || "",
      prazo: act.prazo || "",
      status: act.status || "Pendente",
    });
  };
  const cancelEdit = () => { setEditing(null); setDraft(null); };
  const saveEdit = async (pid) => {
    if (!draft?.acao?.trim()) { toast.error("Campo Ação é obrigatório."); return; }
    setSaving(true);
    try {
      const r = await api.put(`/action-plans/${pid}`, draft);
      setLocalActions((list) => list.map((p) => (p.id === pid ? { ...p, ...r.data } : p)));
      onActionsChanged?.(pid, r.data);
      toast.success("Ação atualizada.");
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };
  const removeAction = async (pid) => {
    if (!window.confirm("Excluir esta ação do plano consolidado?")) return;
    try {
      await api.delete(`/action-plans/${pid}`);
      setLocalActions((list) => list.filter((p) => p.id !== pid));
      onActionsChanged?.(pid, null);
      toast.success("Ação removida.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir.");
    }
  };

  const cellInput = "border border-slate-300 rounded px-1.5 py-1 text-xs w-full";

  return (
    <section id="sec-plano" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Plano de ação consolidado</h2>
      {localActions.length === 0 ? (
        <p className="text-slate-500 italic">Nenhuma ação registrada. Recomenda-se monitoramento periódico e reavaliação em 12 meses.</p>
      ) : (
        <table className="w-full text-xs border border-slate-200" data-testid="aep-plano-table">
          <thead className="bg-slate-50 border-b border-slate-300">
            <tr>
              <th className="text-left p-2 font-bold uppercase">Fator / Dimensão</th>
              <th className="text-left font-bold uppercase">Ação recomendada</th>
              <th className="text-left font-bold uppercase">Prioridade</th>
              <th className="text-left font-bold uppercase">Responsável</th>
              <th className="text-left font-bold uppercase">Prazo</th>
              <th className="text-left font-bold uppercase">Status</th>
              <th className="text-right font-bold uppercase no-print w-20">Ações</th>
            </tr>
          </thead>
          <tbody>
            {localActions.map((act) => {
              const isEd = editing === act.id;
              return (
                <tr key={act.id} className="border-b border-slate-100 align-top" data-testid={`aep-plano-row-${act.id}`}>
                  <td className="py-2 px-2 text-slate-700">
                    {isEd ? <input className={cellInput} value={draft.dimensao} onChange={(e) => setDraft({ ...draft, dimensao: e.target.value })} data-testid={`aep-plano-edit-dim-${act.id}`} /> : (act.dimensao || "—")}
                  </td>
                  <td className="py-2 px-2">
                    {isEd ? <textarea className={cellInput} rows={2} value={draft.acao} onChange={(e) => setDraft({ ...draft, acao: e.target.value })} data-testid={`aep-plano-edit-acao-${act.id}`} /> : act.acao}
                  </td>
                  <td className="py-2 px-2">
                    {isEd ? (
                      <select className={cellInput} value={draft.prioridade} onChange={(e) => setDraft({ ...draft, prioridade: e.target.value })} data-testid={`aep-plano-edit-prio-${act.id}`}>
                        {["Baixa", "Média", "Alta", "Crítica"].map((o) => <option key={o}>{o}</option>)}
                      </select>
                    ) : act.prioridade}
                  </td>
                  <td className="py-2 px-2">
                    {isEd ? <input className={cellInput} value={draft.responsavel} onChange={(e) => setDraft({ ...draft, responsavel: e.target.value })} data-testid={`aep-plano-edit-resp-${act.id}`} /> : (act.responsavel || "—")}
                  </td>
                  <td className="py-2 px-2 font-mono text-[10px]">
                    {isEd ? <input type="date" className={cellInput} value={draft.prazo || ""} onChange={(e) => setDraft({ ...draft, prazo: e.target.value })} data-testid={`aep-plano-edit-prazo-${act.id}`} /> : (act.prazo || "—")}
                  </td>
                  <td className="py-2 px-2">
                    {isEd ? (
                      <select className={cellInput} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid={`aep-plano-edit-status-${act.id}`}>
                        {["Pendente", "Em andamento", "Concluída"].map((o) => <option key={o}>{o}</option>)}
                      </select>
                    ) : act.status}
                  </td>
                  <td className="py-2 px-2 text-right no-print whitespace-nowrap">
                    {isEd ? (
                      <>
                        <button type="button" onClick={() => saveEdit(act.id)} disabled={saving} className="inline-flex items-center gap-1 rounded bg-emerald-700 text-white font-bold px-2 py-1 disabled:opacity-50" data-testid={`aep-plano-save-${act.id}`}>
                          <i className="fa-solid fa-check"></i>
                        </button>
                        <button type="button" onClick={cancelEdit} className="ml-1 inline-flex items-center gap-1 rounded border border-slate-300 text-slate-600 font-bold px-2 py-1" data-testid={`aep-plano-cancel-${act.id}`}>
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => startEdit(act)} className="inline-flex items-center gap-1 rounded border border-emerald-700 text-emerald-800 font-bold px-2 py-1 hover:bg-emerald-50" data-testid={`aep-plano-edit-${act.id}`} title="Editar">
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button type="button" onClick={() => removeAction(act.id)} className="ml-1 inline-flex items-center gap-1 rounded border border-slate-300 text-slate-500 font-bold px-2 py-1 hover:bg-rose-50 hover:text-rose-700" data-testid={`aep-plano-delete-${act.id}`} title="Excluir">
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {cnpj && (
        <EditableAISection
          cnpj={cnpj}
          sectionKey="plano"
          label="Plano de ação consolidado"
          overrides={overrides}
          onChange={onOverrideChange}
        />
      )}
    </section>
  );
}

/* ---------------- Plano Anual SST (anexo) ---------------- */
import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import EditableAISection from "@/pages/aep/EditableAISection";
import SectionAttachments from "@/pages/aep/SectionAttachments";

const _statusColor = {
  "Pendente": "bg-amber-100 text-amber-900 border-amber-300",
  "Em andamento": "bg-sky-100 text-sky-900 border-sky-300",
  "Concluído": "bg-emerald-100 text-emerald-900 border-emerald-300",
};
const _riskColor = {
  "Baixo": "bg-emerald-50 text-emerald-900 border-emerald-300",
  "Médio": "bg-yellow-50 text-yellow-900 border-yellow-300",
  "Alto": "bg-red-50 text-red-900 border-red-300",
};

const ANNUAL_PLAN_DEFAULT_META = {
  months: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
  statuses: ["Pendente", "Em andamento", "Concluído"],
  risks: ["Baixo", "Médio", "Alto"],
};

export function AEPPlanoAnual({ companyId, cnpj, overrides, onOverrideChange, attachments, onAttachmentsChange }) {
  const [actions, setActions] = useState(null);  // null while loading
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(ANNUAL_PLAN_DEFAULT_META);
  const [busyId, setBusyId] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const reload = useCallback(async () => {
    if (!companyId) { setActions([]); setStats(null); return; }
    try {
      const [list, s] = await Promise.all([
        api.get(`/annual-plan/${companyId}`),
        api.get(`/annual-plan/${companyId}/stats`),
      ]);
      setActions(list.data || []);
      setStats(s.data || null);
    } catch {
      setActions([]);
      setStats(null);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
    api.get(`/annual-plan/template`).then((r) => {
      if (r.data) setMeta({
        months: r.data.months || ANNUAL_PLAN_DEFAULT_META.months,
        statuses: r.data.statuses || ANNUAL_PLAN_DEFAULT_META.statuses,
        risks: r.data.risks || ANNUAL_PLAN_DEFAULT_META.risks,
      });
    }).catch(() => {});
  }, [reload]);

  const seedTemplate = async () => {
    if (!companyId) return;
    setSeeding(true);
    try {
      await api.post(`/annual-plan/${companyId}/seed`);
      await reload();
    } catch (e) {
      console.warn("seed failed", e);
    } finally {
      setSeeding(false);
    }
  };

  const addAction = async () => {
    if (!companyId) return;
    setBusyId("new");
    try {
      const payload = { mes: "Janeiro", acao: "Nova ação SST", responsavel: "", status: "Pendente", risco: "Médio", tema: "", evidencia: "" };
      const r = await api.post(`/annual-plan/${companyId}`, payload);
      setActions((cur) => [...(cur || []), r.data]);
      // Refresh stats async (not blocking)
      api.get(`/annual-plan/${companyId}/stats`).then((s) => setStats(s.data || null)).catch(() => {});
    } catch (e) {
      console.warn("add failed", e);
    } finally {
      setBusyId(null);
    }
  };

  const patchAction = async (id, patch) => {
    if (!companyId) return;
    const current = actions.find((a) => a.id === id);
    if (!current) return;
    const next = { ...current, ...patch };
    // Optimistic
    setActions((cur) => cur.map((a) => (a.id === id ? next : a)));
    setBusyId(id);
    try {
      const payload = {
        mes: next.mes, acao: next.acao, responsavel: next.responsavel || "",
        status: next.status, risco: next.risco, tema: next.tema || "", evidencia: next.evidencia || "",
      };
      const r = await api.put(`/annual-plan/${companyId}/${id}`, payload);
      setActions((cur) => cur.map((a) => (a.id === id ? r.data : a)));
      api.get(`/annual-plan/${companyId}/stats`).then((s) => setStats(s.data || null)).catch(() => {});
    } catch (e) {
      // Revert on failure
      setActions((cur) => cur.map((a) => (a.id === id ? current : a)));
      console.warn("update failed", e);
    } finally {
      setBusyId(null);
    }
  };

  const removeAction = async (id) => {
    if (!companyId) return;
    if (typeof window !== "undefined" && !window.confirm("Remover esta ação do Plano Anual?")) return;
    setBusyId(id);
    try {
      await api.delete(`/annual-plan/${companyId}/${id}`);
      setActions((cur) => (cur || []).filter((a) => a.id !== id));
      api.get(`/annual-plan/${companyId}/stats`).then((s) => setStats(s.data || null)).catch(() => {});
    } catch (e) {
      console.warn("delete failed", e);
    } finally {
      setBusyId(null);
    }
  };

  if (actions === null) {
    return (
      <section id="sec-plano-anual" className="page-break mb-12">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">
          Plano Anual SST <span className="text-slate-500 font-medium text-base">(anexo)</span>
        </h2>
        <p className="text-sm text-slate-500">Carregando plano anual…</p>
      </section>
    );
  }

  return (
    <section id="sec-plano-anual" className="page-break mb-12">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Plano Anual SST <span className="text-slate-500 font-medium text-base">(anexo)</span>
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Programação anual de campanhas, DDS, inspeções e treinamentos que dão suporte às
            recomendações deste AEP e atendem à NR-01 / NR-05 / NR-07 / NR-23.
          </p>
        </div>
        {companyId && (
          <div className="no-print flex gap-2 shrink-0">
            {actions.length === 0 && (
              <button
                type="button"
                onClick={seedTemplate}
                disabled={seeding}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded"
                data-testid="aep-annual-seed"
              >
                <i className="fa-solid fa-wand-magic-sparkles mr-1"></i>
                {seeding ? "Gerando…" : "Gerar template Sollute"}
              </button>
            )}
            <button
              type="button"
              onClick={addAction}
              disabled={busyId === "new"}
              className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded"
              data-testid="aep-annual-add"
            >
              <i className="fa-solid fa-plus mr-1"></i> Adicionar ação
            </button>
          </div>
        )}
      </div>

      {actions.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
          Nenhuma ação cadastrada no Plano Anual desta empresa. Clique em
          <strong> &quot;Gerar template Sollute&quot;</strong> acima para importar as 12 campanhas padrão,
          ou <strong>&quot;Adicionar ação&quot;</strong> para cadastrar a primeira manualmente.
        </div>
      ) : (
        <>
          {/* KPIs compactos */}
          {stats && (
            <div className="grid grid-cols-4 gap-3 mb-5">
              <div className="border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                <div className="font-display text-xl font-extrabold text-slate-900">{stats.total}</div>
              </div>
              <div className="border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Concluídas</div>
                <div className="font-display text-xl font-extrabold text-emerald-700">{stats.done_percent}%</div>
              </div>
              <div className="border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Saúde Mental</div>
                <div className="font-display text-xl font-extrabold text-sky-700">{stats.psico}</div>
              </div>
              <div className="border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Alta prioridade</div>
                <div className="font-display text-xl font-extrabold text-rose-700">{stats.alta}</div>
              </div>
            </div>
          )}

          {/* Tabela editável de ações */}
          <table className="w-full text-xs border-collapse" data-testid="aep-annual-plan-table">
            <thead>
              <tr className="bg-slate-100 border-b-2 border-slate-300">
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wider">Mês</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wider">Ação / Campanha</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wider">Responsável</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wider">Tema</th>
                <th className="text-center px-2 py-2 font-bold uppercase text-[10px] tracking-wider">Status</th>
                <th className="text-center px-2 py-2 font-bold uppercase text-[10px] tracking-wider">Risco</th>
                <th className="text-center px-2 py-2 font-bold uppercase text-[10px] tracking-wider no-print">·</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <AnnualActionRow
                  key={a.id}
                  action={a}
                  meta={meta}
                  busy={busyId === a.id}
                  onChange={(patch) => patchAction(a.id, patch)}
                  onRemove={() => removeAction(a.id)}
                />
              ))}
            </tbody>
          </table>

          <p className="mt-4 text-[11px] text-slate-500 italic">
            Plano sujeito a revisão semestral conforme indicadores de absenteísmo, acidentes,
            queixas psicossociais e auditorias internas.
          </p>
        </>
      )}
      {cnpj && (
        <EditableAISection
          cnpj={cnpj}
          sectionKey="plano_anual"
          label="Plano Anual SST"
          overrides={overrides}
          onChange={onOverrideChange}
        />
      )}
      {cnpj && (
        <SectionAttachments
          cnpj={cnpj}
          sectionKey="plano_anual"
          label="Plano Anual SST"
          attachments={attachments}
          onChange={onAttachmentsChange}
        />
      )}
    </section>
  );
}

function AnnualActionRow({ action, meta, busy, onChange, onRemove }) {
  const [draftAcao, setDraftAcao] = useState(action.acao || "");
  const [draftResp, setDraftResp] = useState(action.responsavel || "");
  const [draftTema, setDraftTema] = useState(action.tema || "");
  useEffect(() => { setDraftAcao(action.acao || ""); }, [action.acao]);
  useEffect(() => { setDraftResp(action.responsavel || ""); }, [action.responsavel]);
  useEffect(() => { setDraftTema(action.tema || ""); }, [action.tema]);

  const commit = (field, value) => {
    if ((value || "") !== (action[field] || "")) onChange({ [field]: value });
  };

  const statusColor = _statusColor[action.status] || "border-slate-300 bg-slate-50 text-slate-700";
  const riskColor = _riskColor[action.risco] || "border-slate-300 bg-slate-50 text-slate-700";

  return (
    <tr className="border-b border-slate-200 avoid-break" data-testid={`aep-annual-row-${action.id}`}>
      <td className="px-2 py-2 font-semibold text-slate-900 whitespace-nowrap">
        {/* PRINT: just the text. SCREEN: editable select */}
        <span className="print:inline hidden">{action.mes}</span>
        <select
          value={action.mes}
          onChange={(e) => onChange({ mes: e.target.value })}
          disabled={busy}
          className="no-print bg-transparent border border-transparent hover:border-slate-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 text-xs font-semibold"
          data-testid={`aep-annual-mes-${action.id}`}
        >
          {meta.months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 text-slate-700">
        <span className="print:inline hidden">{action.acao}</span>
        <input
          type="text"
          value={draftAcao}
          onChange={(e) => setDraftAcao(e.target.value)}
          onBlur={() => commit("acao", draftAcao.trim())}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          disabled={busy}
          className="no-print w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 text-xs"
          data-testid={`aep-annual-acao-${action.id}`}
        />
      </td>
      <td className="px-2 py-2 text-slate-600">
        <span className="print:inline hidden">{action.responsavel || "—"}</span>
        <input
          type="text"
          value={draftResp}
          onChange={(e) => setDraftResp(e.target.value)}
          onBlur={() => commit("responsavel", draftResp.trim())}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          disabled={busy}
          placeholder="Gestão / SST"
          className="no-print w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 text-xs"
          data-testid={`aep-annual-resp-${action.id}`}
        />
      </td>
      <td className="px-2 py-2 text-slate-600">
        <span className="print:inline hidden">{action.tema || "—"}</span>
        <input
          type="text"
          value={draftTema}
          onChange={(e) => setDraftTema(e.target.value)}
          onBlur={() => commit("tema", draftTema.trim())}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          disabled={busy}
          placeholder="Saúde Mental, NR-05…"
          className="no-print w-full bg-transparent border border-transparent hover:border-slate-300 focus:border-emerald-400 focus:outline-none rounded px-1 py-0.5 text-xs"
          data-testid={`aep-annual-tema-${action.id}`}
        />
      </td>
      <td className="px-2 py-2 text-center">
        <span className={`print:inline hidden border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>{action.status}</span>
        <select
          value={action.status}
          onChange={(e) => onChange({ status: e.target.value })}
          disabled={busy}
          className={`no-print border px-1 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded focus:outline-none ${statusColor}`}
          data-testid={`aep-annual-status-${action.id}`}
        >
          {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 text-center">
        <span className={`print:inline hidden border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${riskColor}`}>{action.risco}</span>
        <select
          value={action.risco}
          onChange={(e) => onChange({ risco: e.target.value })}
          disabled={busy}
          className={`no-print border px-1 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded focus:outline-none ${riskColor}`}
          data-testid={`aep-annual-risco-${action.id}`}
        >
          {meta.risks.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 text-center no-print">
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="text-rose-600 hover:text-rose-800 disabled:opacity-50"
          title="Remover ação"
          data-testid={`aep-annual-del-${action.id}`}
        >
          <i className="fa-solid fa-trash text-xs"></i>
        </button>
      </td>
    </tr>
  );
}

/* ---------------- Conclusão ---------------- */
export function AEPConclusao({ data, score100, criticalCount, cnpj, overrides, onOverrideChange }) {
  return (
    <section id="sec-conclusao" className="mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">Conclusão técnica</h2>
      <p className="text-slate-700 leading-relaxed">
        Foram consolidadas <strong>{data.response_count}</strong> avaliação(ões) psicossocial(is) individual(is),
        com percentual médio geral de <strong>{score100}%</strong> e classificação <strong>{data.overall.label}</strong>.
        Foram identificadas <strong>{criticalCount}</strong> respostas críticas nos documentos importados.
        Os resultados, em formato de AEP, indicam a necessidade de inserção dos fatores psicossociais no inventário de riscos do PGR,
        conforme lógica de gerenciamento da NR-01 e avaliação da organização do trabalho prevista na NR-17,
        com definição de responsáveis, prazos, evidências e acompanhamento do plano de ação.
        A conclusão final deve ser validada pelo responsável técnico com base nas condições reais de trabalho,
        entrevistas, indicadores ocupacionais e demais evidências disponíveis.
      </p>
      {cnpj && (
        <EditableAISection
          cnpj={cnpj}
          sectionKey="conclusao"
          label="Conclusão técnica"
          overrides={overrides}
          onChange={onOverrideChange}
        />
      )}
    </section>
  );
}

/* ---------------- Assinaturas ---------------- */
export function AEPAssinaturas({ a, company, date, hash }) {
  const companySig = a.company_signature_base64;
  const techSig    = a.technical_signature_base64;
  const companySigner = a.company_signer || "";
  const techSigner    = a.technical_signer || a.responsavel || company.responsavel_tecnico || "Responsável técnico";
  const signedAt = a.signed_at ? new Date(a.signed_at).toLocaleString("pt-BR") : null;

  return (
    <section id="sec-assinaturas" className="mb-8">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6 text-slate-900">Assinatura eletrônica e validação final</h2>
      <div className="grid grid-cols-2 gap-16 mt-12">
        <div data-testid="aep-sign-company">
          <div className="h-20 flex items-end justify-center mb-1">
            {companySig
              ? <img src={companySig} alt="Assinatura da empresa" className="max-h-20 max-w-full object-contain" />
              : <div className="text-[10px] text-slate-400 uppercase tracking-wider">Sem assinatura registrada</div>}
          </div>
          <div className="border-t border-slate-700 pt-2 text-center text-sm font-semibold">
            {companySigner || "Responsável pela empresa"}
          </div>
          <div className="text-xs text-slate-500 text-center mt-1">Ciência do plano de ação · NR-01.</div>
        </div>
        <div data-testid="aep-sign-tech">
          <div className="h-20 flex items-end justify-center mb-1">
            {techSig
              ? <img src={techSig} alt="Assinatura técnica" className="max-h-20 max-w-full object-contain" />
              : <div className="text-[10px] text-slate-400 uppercase tracking-wider">Sem assinatura registrada</div>}
          </div>
          <div className="border-t border-slate-700 pt-2 text-center text-sm font-semibold">{techSigner}</div>
          <div className="text-xs text-slate-500 text-center mt-1">Responsável técnico SST.</div>
        </div>
      </div>

      {/* QR validation card */}
      <div className="mt-12 border border-slate-300 p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="aep-sign-qr">
        <div className="bg-white border border-slate-200 p-2 shrink-0">
          <QRCodeSVG value={validateUrl(a.id)} size={120} level="M" includeMargin={false} />
        </div>
        <div className="text-sm text-slate-700 flex-1">
          <div className="overline mb-2 text-emerald-900">Validação de autenticidade</div>
          <p className="leading-relaxed">
            Aponte a câmera para o QR Code acima ou acesse a URL abaixo para confirmar que este relatório
            consta nos registros da plataforma Sollute NR-01, com os mesmos metadados de empresa, score,
            número de respostas e assinaturas.
          </p>
          <div className="font-mono text-xs mt-3 break-all text-emerald-900">{validateUrl(a.id)}</div>
          {signedAt && (
            <div className="text-xs text-slate-500 mt-2">
              <i className="fa-solid fa-circle-check text-emerald-700 mr-1"></i>
              Documento assinado eletronicamente em <strong>{signedAt}</strong>.
            </div>
          )}
        </div>
      </div>

      <div className="text-[10px] text-slate-400 text-center mt-8">
        Documento gerado em {date} · Sollute NR-01 Platform · {hash}
      </div>
    </section>
  );
}


/* ---------------- Absenteísmo (anexo opcional) ---------------- */
export function AEPAbsenteismo({ companyId, cnpj, overrides, onOverrideChange, attachments, onAttachmentsChange }) {
  const [body, setBody] = useState(null);

  useEffect(() => {
    if (!companyId) { setBody({ kpis: {}, by_setor: [], top_cids: [] }); return; }
    let cancelled = false;
    api.get(`/absenteeism/dashboard?company_id=${companyId}`)
      .then(({ data }) => { if (!cancelled) setBody(data || null); })
      .catch(() => { if (!cancelled) setBody({ kpis: {}, by_setor: [], top_cids: [] }); });
    return () => { cancelled = true; };
  }, [companyId]);

  if (body === null) {
    return (
      <section id="sec-absenteismo" className="page-break mb-12">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">
          Absenteísmo NR-01 <span className="text-slate-500 font-medium text-base">(anexo)</span>
        </h2>
        <p className="text-sm text-slate-500">Carregando dados de absenteísmo…</p>
      </section>
    );
  }

  const kpis = body.kpis || {};
  const bySetor = body.by_setor || [];
  const topCids = body.top_cids || [];
  const noData = !kpis.total_certificates || kpis.total_certificates === 0;

  if (noData) {
    return (
      <section id="sec-absenteismo" className="page-break mb-12">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-2 text-slate-900">
          Absenteísmo NR-01 <span className="text-slate-500 font-medium text-base">(anexo)</span>
        </h2>
        <div className="bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
          Nenhum atestado cadastrado no módulo de Absenteísmo desta empresa.
          Use a aba <strong>Absenteísmo</strong> dentro do Hub do Cliente para registrar afastamentos.
        </div>
        {cnpj && (
          <EditableAISection
            cnpj={cnpj}
            sectionKey="absenteismo"
            label="Absenteísmo NR-01"
            overrides={overrides}
            onChange={onOverrideChange}
          />
        )}
        {cnpj && (
          <SectionAttachments
            cnpj={cnpj}
            sectionKey="absenteismo"
            label="Absenteísmo NR-01"
            attachments={attachments}
            onChange={onAttachmentsChange}
          />
        )}
      </section>
    );
  }

  const taxa = (kpis.absenteeism_rate_pct || 0);
  return (
    <section id="sec-absenteismo" className="page-break mb-12">
      <h2 className="font-display text-2xl font-extrabold tracking-tight mb-2 text-slate-900">
        Absenteísmo NR-01 <span className="text-slate-500 font-medium text-base">(anexo)</span>
      </h2>
      <p className="text-sm text-slate-600 mb-4">
        Correlação entre afastamentos médicos (CID-10), setor, GHE e os fatores psicossociais identificados
        no NR-01. Indicadores apurados no período padrão da empresa.
      </p>

      <div className="grid grid-cols-4 gap-3 mb-5" data-testid="aep-absenteismo-kpis">
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Atestados</div>
          <div className="font-display text-xl font-extrabold text-slate-900">{kpis.total_certificates || 0}</div>
        </div>
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Dias perdidos</div>
          <div className="font-display text-xl font-extrabold text-rose-700">{kpis.total_days_lost || 0}</div>
        </div>
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Colaboradores afetados</div>
          <div className="font-display text-xl font-extrabold text-amber-700">{kpis.employees_affected || 0}</div>
        </div>
        <div className="border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Taxa de absenteísmo</div>
          <div className="font-display text-xl font-extrabold text-sky-700">{Number(taxa).toFixed(2)}%</div>
        </div>
      </div>

      {topCids.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">Principais CIDs (top 5)</h3>
          <table className="w-full text-xs border-collapse" data-testid="aep-absenteismo-cids">
            <thead>
              <tr className="bg-slate-100 border-b-2 border-slate-300">
                <th className="text-left px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">CID</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">Grupo</th>
                <th className="text-right px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">Ocorrências</th>
                <th className="text-right px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">Dias</th>
              </tr>
            </thead>
            <tbody>
              {topCids.slice(0, 5).map((c, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 font-mono">{c.cid}</td>
                  <td className="px-2 py-1.5">{c.group || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{c.count}</td>
                  <td className="px-2 py-1.5 text-right">{c.days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bySetor.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">Distribuição por setor</h3>
          <table className="w-full text-xs border-collapse" data-testid="aep-absenteismo-setor">
            <thead>
              <tr className="bg-slate-100 border-b-2 border-slate-300">
                <th className="text-left px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">Setor</th>
                <th className="text-right px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">Atestados</th>
                <th className="text-right px-2 py-1.5 font-bold uppercase text-[10px] tracking-wider">Dias</th>
              </tr>
            </thead>
            <tbody>
              {bySetor.slice(0, 8).map((s, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1.5">{s.setor || "—"}</td>
                  <td className="px-2 py-1.5 text-right">{s.count}</td>
                  <td className="px-2 py-1.5 text-right">{s.days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cnpj && (
        <EditableAISection
          cnpj={cnpj}
          sectionKey="absenteismo"
          label="Absenteísmo NR-01"
          overrides={overrides}
          onChange={onOverrideChange}
        />
      )}
      {cnpj && (
        <SectionAttachments
          cnpj={cnpj}
          sectionKey="absenteismo"
          label="Absenteísmo NR-01"
          attachments={attachments}
          onChange={onAttachmentsChange}
        />
      )}
    </section>
  );
}

/* ---------------- Recomendações Prioritárias (gerável inline) ---------------- */
export function AEPRecomendacoesIA({ assessmentId, assessmentIds, cnpj, overrides, onOverrideChange }) {
  // Candidate assessment IDs to look up (and to generate against). In the
  // single-assessment AEP, this is just `[assessmentId]`. In the per-CNPJ
  // consolidated AEP, the caller passes the full list of underlying
  // assessments — we pick the first one that already has an analysis, or
  // the first valid one for generation.
  const candidates = useMemo(() => {
    const arr = Array.isArray(assessmentIds) ? assessmentIds.filter(Boolean) : [];
    if (assessmentId && !arr.includes(assessmentId)) arr.unshift(assessmentId);
    return arr.filter((x) => typeof x === "string" && !x.startsWith("cnpj-"));
  }, [assessmentId, assessmentIds]);

  const [analysis, setAnalysis] = useState(null);
  const [sourceAid, setSourceAid] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const loadFromCandidates = useCallback(async () => {
    if (candidates.length === 0) { setAnalysis({ items: [], conclusao: "" }); return; }
    for (const aid of candidates) {
      try {
        const list = await api.get(`/assessments/${aid}/ai-analyses`);
        if (list.data && list.data.length > 0) {
          const latest = await api.get(`/assessments/${aid}/ai-analyses/${list.data[0].id}`);
          const a = latest.data?.analysis || {};
          setAnalysis({
            items: a.recomendacoes_prioritarias || [],
            conclusao: a.conclusao_tecnica || "",
            edited_at: latest.data?.edited_at || null,
          });
          setSourceAid(aid);
          return;
        }
      } catch (_) { /* try next */ }
    }
    setAnalysis({ items: [], conclusao: "" });
    setSourceAid(candidates[0] || null);
  }, [candidates]);

  useEffect(() => { loadFromCandidates(); }, [loadFromCandidates]);

  const pollJob = async (jobId) => {
    const deadline = Date.now() + 90_000; // up to ~90s
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2200));
      const res = await api.get(`/assessments/ai-jobs/${jobId}`);
      if (res.data.status === "done") return true;
      if (res.data.status === "error") throw new Error(res.data.error || "Falha ao gerar.");
    }
    throw new Error("Tempo esgotado aguardando a geração.");
  };

  const generate = async () => {
    setError("");
    if (candidates.length === 0) {
      setError("Nenhuma avaliação válida encontrada para gerar as recomendações.");
      return;
    }
    setGenerating(true);
    try {
      let lastErr = null;
      for (const aid of candidates) {
        try {
          const r = await api.post(`/assessments/${aid}/ai-analysis`);
          if (r.data?.status === "done" || r.data?.cached) {
            // Cached result, no job to poll
            await loadFromCandidates();
            return;
          }
          if (r.data?.job_id) {
            await pollJob(r.data.job_id);
            await loadFromCandidates();
            return;
          }
        } catch (e) {
          lastErr = e;
          // Try the next candidate (e.g., this one has no responses)
        }
      }
      throw lastErr || new Error("Não foi possível iniciar a geração.");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erro ao gerar recomendações.");
    } finally {
      setGenerating(false);
    }
  };

  if (analysis === null) {
    return (
      <section id="sec-recomendacoes-ia" className="page-break mb-12">
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-4 text-slate-900">
          Recomendações Prioritárias
        </h2>
        <p className="text-sm text-slate-500">Carregando recomendações…</p>
      </section>
    );
  }

  const canGenerate = candidates.length > 0;

  if (analysis.items.length === 0) {
    return (
      <section id="sec-recomendacoes-ia" className="page-break mb-12">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Recomendações Prioritárias
          </h2>
          {canGenerate && (
            <button
              type="button"
              onClick={generate}
              disabled={generating}
              className="no-print bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded"
              data-testid="aep-recomendacoes-generate"
            >
              <i className={`fa-solid ${generating ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"} mr-1`}></i>
              {generating ? "Gerando…" : "Gerar recomendações"}
            </button>
          )}
        </div>
        <div className="bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
          {canGenerate
            ? <>Nenhuma recomendação cadastrada ainda. Clique em <strong>&quot;Gerar recomendações&quot;</strong> para que o sistema produza recomendações prioritárias técnicas com base no diagnóstico psicossocial deste CNPJ.</>
            : <>Para gerar as recomendações é necessário ao menos uma avaliação com respostas válidas vinculada a este CNPJ.</>}
        </div>
        {error && (
          <div className="no-print mt-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs p-3 rounded" data-testid="aep-recomendacoes-error">
            <i className="fa-solid fa-triangle-exclamation mr-1"></i> {error}
          </div>
        )}
        {cnpj && (
          <EditableAISection
            cnpj={cnpj}
            sectionKey="recomendacoes_ia"
            label="Recomendações Prioritárias"
            overrides={overrides}
            onChange={onOverrideChange}
          />
        )}
      </section>
    );
  }

  return (
    <section id="sec-recomendacoes-ia" className="page-break mb-12">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
          Recomendações Prioritárias
        </h2>
        {canGenerate && (
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            className="no-print bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-bold px-3 py-1.5 rounded"
            data-testid="aep-recomendacoes-regenerate"
            title="Recalcular com base nos dados atuais"
          >
            <i className={`fa-solid ${generating ? "fa-spinner fa-spin" : "fa-rotate"} mr-1`}></i>
            {generating ? "Gerando…" : "Regerar"}
          </button>
        )}
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Recomendações ordenadas por prioridade técnica e horizonte de implementação,
        com base no diagnóstico psicossocial consolidado deste CNPJ.
        {analysis.edited_at && (
          <span className="ml-2 text-xs text-emerald-700 font-semibold">
            <i className="fa-solid fa-pen-to-square mr-1"></i>
            Editadas manualmente
          </span>
        )}
      </p>
      {error && (
        <div className="no-print mb-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs p-3 rounded">
          <i className="fa-solid fa-triangle-exclamation mr-1"></i> {error}
        </div>
      )}

      <ol className="space-y-3" data-testid="aep-recomendacoes-ia-list">
        {analysis.items.map((r, i) => (
          <li key={i} className="flex gap-3 bg-emerald-50/40 border-l-4 border-emerald-600 p-3">
            <span className="shrink-0 inline-flex h-7 w-7 items-center justify-center bg-emerald-700 text-white text-sm font-bold">
              {r.ordem || i + 1}
            </span>
            <div className="flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="font-bold text-slate-900 text-sm">{r.titulo}</h4>
                {r.horizonte && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-800 uppercase tracking-wider">
                    {r.horizonte}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{r.descricao}</p>
            </div>
          </li>
        ))}
      </ol>
      {cnpj && (
        <EditableAISection
          cnpj={cnpj}
          sectionKey="recomendacoes_ia"
          label="Recomendações Prioritárias"
          overrides={overrides}
          onChange={onOverrideChange}
        />
      )}
    </section>
  );
}


/* ---------------- Matriz de Risco Consolidada (Geral / GHE / Setor / Individual) ----------------
 * Renderiza a matriz de risco em múltiplos cortes em sequência, conforme as
 * views ativas. Pensado para o AEP por CNPJ — consolida todas as avaliações.
 *
 * Props:
 *   geral         : objeto consolidado {dimensions, overall_score, response_count, critical_count}
 *   byGhe         : array [{ghe_setor, dimensions, overall_score, response_count, critical_count}]
 *   perAssessment : array [{id, titulo, ghe_setor, funcao, dimensions, overall_score, response_count, critical_count, matrix_type}]
 *   views         : ['geral'|'ghe'|'setor'|'individual'] — quais cortes mostrar
 *   matrixType    : 'pxs' (AIHA) | 'fmea' — passado para o RiskMatrix
 *   expectedWorkers : usado pelo FMEA
 */
export function AEPMatrizConsolidada({
  geral, byGhe = [], perAssessment = [],
  views = ["geral", "ghe", "individual"],
  matrixType = "pxs", expectedWorkers = 0,
}) {
  const showView = (k) => views.includes(k);
  const isFmea = matrixType === "fmea";

  const renderMatrix = (block, key) => {
    const data = {
      dimensions: block.dimensions || [],
      overall_score: block.overall_score || 0,
      response_count: block.response_count || 0,
      assessment: { aiha_strict: false },
    };
    const critical = block.critical_count || 0;
    return isFmea ? (
      <RiskMatrixFMEA
        key={key}
        overallScore={data.overall_score}
        dimensions={data.dimensions}
        criticalCount={critical}
        totalResponses={data.response_count}
        expectedWorkers={expectedWorkers}
        editable={false}
        showLegend={false}
        strict={false}
      />
    ) : (
      <RiskMatrix
        key={key}
        overallScore={data.overall_score}
        dimensions={data.dimensions}
        criticalCount={critical}
        totalResponses={data.response_count}
        showLegend={false}
        strict={false}
      />
    );
  };

  return (
    <section id="sec-matriz-consolidada" className="page-break mb-12">
      <header className="mb-4 border-b-2 border-emerald-700 pb-2">
        <div className="overline text-emerald-800 mb-1">Análise consolidada por CNPJ</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
          {isFmea ? "Matriz FMEA Consolidada" : "Matriz de Risco AIHA Consolidada"}
        </h2>
        <p className="text-xs text-slate-600 mt-1">
          Visões selecionadas: <strong>{views.map((v) => ({geral:"Geral", ghe:"GHE", setor:"Setor", individual:"Individual"}[v])).join(" · ")}</strong>.
          {" "}Todas as avaliações deste CNPJ foram consolidadas — {perAssessment.length} {perAssessment.length === 1 ? "avaliação" : "avaliações"}.
        </p>
      </header>

      {/* --- GERAL --- */}
      {showView("geral") && geral && (
        <article className="mb-8" data-testid="aep-matriz-geral">
          <div className="flex items-baseline justify-between mb-2 border-l-4 border-emerald-700 pl-3">
            <h3 className="font-display text-lg font-bold text-emerald-900">Visão Geral — todas as avaliações</h3>
            <span className="text-xs text-slate-500">
              {geral.response_count || 0} respostas · score médio {(geral.overall_score || 0).toFixed(2)}
            </span>
          </div>
          {renderMatrix(geral, "geral")}
        </article>
      )}

      {/* --- POR GHE (e por SETOR, mesma fonte de dados ghe_setor) --- */}
      {(showView("ghe") || showView("setor")) && byGhe.length > 0 && (
        <article className="mb-8" data-testid="aep-matriz-ghe">
          <div className="flex items-baseline justify-between mb-2 border-l-4 border-amber-600 pl-3">
            <h3 className="font-display text-lg font-bold text-amber-900">
              Por {showView("ghe") && showView("setor") ? "GHE / Setor" : showView("ghe") ? "GHE" : "Setor"}
            </h3>
            <span className="text-xs text-slate-500">{byGhe.length} {byGhe.length === 1 ? "grupo" : "grupos"}</span>
          </div>
          <div className="space-y-6">
            {byGhe.map((g, i) => (
              <div key={`${g.ghe_setor}-${i}`} className="border border-slate-200 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <h4 className="text-sm font-bold text-slate-900">
                    <i className="fa-solid fa-industry text-amber-700 mr-1.5"></i>
                    {g.ghe_setor}
                  </h4>
                  <span className="text-xs text-slate-600">
                    {g.assessment_count || 0} {g.assessment_count === 1 ? "avaliação" : "avaliações"} ·
                    {" "}{g.response_count || 0} respostas ·
                    {" "}score {(g.overall_score || 0).toFixed(2)} ·
                    {" "}<span className="font-semibold" style={{ color: g.overall?.color || "#475569" }}>{g.overall?.label || "Sem dados"}</span>
                  </span>
                </div>
                {renderMatrix(g, `ghe-${i}`)}
              </div>
            ))}
          </div>
        </article>
      )}

      {/* --- INDIVIDUAL (uma matriz por avaliação) --- */}
      {showView("individual") && perAssessment.length > 0 && (
        <article data-testid="aep-matriz-individual">
          <div className="flex items-baseline justify-between mb-2 border-l-4 border-sky-700 pl-3">
            <h3 className="font-display text-lg font-bold text-sky-900">Por Avaliação Individual</h3>
            <span className="text-xs text-slate-500">{perAssessment.length} {perAssessment.length === 1 ? "avaliação" : "avaliações"}</span>
          </div>
          <div className="space-y-6">
            {perAssessment.map((a, i) => (
              <div key={a.id || i} className="border border-slate-200 p-3 page-break-inside-avoid">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      <i className="fa-solid fa-clipboard-check text-sky-700 mr-1.5"></i>
                      {a.titulo || "Avaliação sem título"}
                    </h4>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      GHE/Setor: <strong>{a.ghe_setor || "—"}</strong> ·
                      Função: <strong>{a.funcao || "—"}</strong> ·
                      Trabalhadores: <strong>{a.n_trabalhadores || 0}</strong>
                    </div>
                  </div>
                  <span className="text-xs text-slate-600 text-right">
                    {a.response_count || 0} respostas ·
                    {" "}score {(a.overall_score || 0).toFixed(2)} ·
                    {" "}<span className="font-semibold" style={{ color: a.overall?.color || "#475569" }}>{a.overall?.label || "Sem dados"}</span>
                  </span>
                </div>
                {renderMatrix(a, `ind-${i}`)}
              </div>
            ))}
          </div>
        </article>
      )}

      {/* Empty state: nenhuma view tem dados */}
      {!geral && byGhe.length === 0 && perAssessment.length === 0 && (
        <div className="bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
          Nenhuma avaliação com respostas suficientes neste CNPJ para gerar a matriz consolidada.
        </div>
      )}
    </section>
  );
}
